/**
 * Screens 03a + 03b — Account in review.
 *
 * Role-aware. Same shell renders for IFA + Individual; the checklist
 * and copy differ. Pure wait state — onSnapshot in AuthProvider will
 * flip the user to their workspace home as soon as admin updates
 * status to "active", so this screen is fire-and-forget for the user.
 *
 * They can leave the tab; an activation email lands when status flips
 * (out-of-band, separate transactional pipeline — not implemented in
 * this v1 commit).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowRight,
  IconCheck,
  IconSparkle,
  IconWA,
} from "@/components/icons";
import { useAuth } from "@/auth";
import { useRecordsMode, homeFor } from "@/lib/routes";

export function ApprovalPending() {
  return useRecordsMode() ? <RecordsPending /> : <ResearchApprovalPending />;
}
function RecordsPending() {
  const { signOut } = useAuth();
  return <main style={{ maxWidth: 620, margin: "48px auto", padding: 24 }}>
    <h1>Practice account awaiting approval</h1><p>Your Portfolio Records workspace is awaiting account review. Approval provides access to recordkeeping software, not investment-advisory services.</p>
    <a href="mailto:support@stocksenseai.in?subject=Portfolio%20Records%20account%20review">Contact support about account review</a>
    <p><button onClick={() => window.location.reload()}>Check status</button> <button onClick={() => void signOut()}>Sign out</button></p>
  </main>;
}
function ResearchApprovalPending() {
  const nav = useNavigate();
  const { user, profile, signOut } = useAuth();

  // If status flipped to active while this screen is mounted (live
  // Firestore subscription in AuthProvider), redirect to the right
  // workspace home. Belt-and-braces: the router also gates this in
  // App.tsx, so users can't deep-link here once active.
  React.useEffect(() => {
    if (profile?.status === "active") {
      nav(homeFor(profile.role), {
        replace: true,
      });
    }
  }, [profile?.status, profile?.role, nav]);

  const role = profile?.role ?? "individual";
  const isIFA = role === "ifa" || role === "ra";
  const name = profile?.displayName ?? user?.email ?? "there";
  const firstName = name.split(" ")[0];
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const email = user?.email ?? "your registered email";

  const submitted = isIFA ? "SEBI registration submitted" : "Account submitted";
  const submittedDesc = isIFA
    ? `You signed in as ${name} and submitted ${profile?.sebiRegistration ?? "your INA number"} for review.`
    : "You signed in with Google. We received your account details.";

  const checks = isIFA
    ? [
        {
          l: "SEBI registration",
          d: "Looking up your INA number in the active public registry.",
          state: "run" as const,
        },
        {
          l: "Profile match",
          d: "Reconciling your Google profile with the registered name.",
          state: "idle" as const,
        },
        {
          l: "Public regulatory flags",
          d: "Any actions filed against this registration in the last year.",
          state: "idle" as const,
        },
      ]
    : [
        {
          l: "Google account",
          d: "Confirming the Google profile you signed in with.",
          state: "run" as const,
        },
        {
          l: "Account history",
          d: "Standard checks every new account goes through.",
          state: "idle" as const,
        },
      ];

  return (
    <div className="col" style={{ minHeight: "100%", background: "var(--paper-2)" }}>
      <header className="appnav" style={{ padding: "18px 32px" }}>
        <div className="wordmark">
          <span className="bm">S</span>
          StockSense <span className="ai">AI</span> <span className="pro">Pro</span>
        </div>
        <div style={{ flex: 1 }} />
        <a
          href="https://wa.me/917757025257"
              target="_blank" rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            signOut();
          }}
          style={{
            color: "var(--ink-mid)",
            textDecoration: "none",
            fontSize: 13,
          }}
        >
          Sign out
        </a>
      </header>

      <main
        style={{
          flex: 1,
          padding: "44px 48px 56px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 960, width: "100%" }}>
          <div className="row gap-4 center" style={{ marginBottom: 30 }}>
            <div
              className="av lg"
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                fontSize: 20,
                background: "var(--warn-bg)",
                color: "var(--warn-text)",
              }}
            >
              {initials}
            </div>
            <div>
              <div className="kicker">
                <span className="sq" />
                Account setup · in review
              </div>
              <h1
                className="h-display"
                style={{ fontSize: 30, marginTop: 6, lineHeight: 1.1 }}
              >
                Thanks, {firstName}. Your account is in review.
              </h1>
              <p
                className="muted"
                style={{
                  fontSize: 14,
                  marginTop: 8,
                  lineHeight: 1.55,
                  maxWidth: "64ch",
                }}
              >
                We'll email you at{" "}
                <strong style={{ color: "var(--ink)" }}>{email}</strong> as
                soon as your workspace is ready. You don't need to keep this
                tab open.
              </p>
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 18 }}
            className="grid-2"
          >
            <div className="col gap-3">
              <div className="card padded" style={{ padding: 24 }}>
                <div className="lbl" style={{ marginBottom: 18 }}>
                  Where you are
                </div>
                <div className="status-tl">
                  <div className="step done">
                    <div className="dot">
                      <IconCheck />
                    </div>
                    <div className="body">
                      <div className="title">{submitted}</div>
                      <div className="desc">{submittedDesc}</div>
                      <div className="meta">Just now</div>
                    </div>
                  </div>
                  <div className="step run">
                    <div className="dot">
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "currentColor",
                          display: "inline-block",
                        }}
                      />
                    </div>
                    <div className="body">
                      <div className="title">Review in progress</div>
                      <div className="desc">
                        Our team is checking the details below.{" "}
                        {isIFA
                          ? "No further action is needed from you."
                          : "We'll reach out only if anything is unclear."}
                      </div>
                      <div className="meta">Currently here</div>
                    </div>
                  </div>
                  <div className="step idle">
                    <div className="dot">3</div>
                    <div className="body">
                      <div className="title">Workspace activated</div>
                      <div className="desc">
                        You'll receive an email; sign back in and you're live.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card padded" style={{ padding: 24 }}>
                <div className="lbl" style={{ marginBottom: 14 }}>
                  What we're reviewing
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {checks.map((c, i) => (
                    <li
                      key={i}
                      className="row gap-3"
                      style={{ alignItems: "flex-start" }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background:
                            c.state === "run"
                              ? "var(--warn-bg)"
                              : "var(--paper-3)",
                          color:
                            c.state === "run"
                              ? "var(--warn-text)"
                              : "var(--ink-light)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          fontSize: 11,
                          fontFamily: "var(--mono)",
                          fontWeight: 600,
                          marginTop: 1,
                        }}
                      >
                        {i + 1}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "var(--ink)",
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {c.l}
                        </div>
                        <div
                          className="muted"
                          style={{
                            fontSize: 12.5,
                            marginTop: 3,
                            lineHeight: 1.55,
                          }}
                        >
                          {c.d}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col gap-3">
              <div className="card padded">
                <div className="row gap-2 center" style={{ marginBottom: 10 }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "var(--gain-bg)",
                      color: "var(--gain-text)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IconCheck />
                  </span>
                  <div className="lbl">Confirmation email sent</div>
                </div>
                <p
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink-mid)",
                    lineHeight: 1.55,
                  }}
                >
                  Receipt sent to{" "}
                  <strong style={{ color: "var(--ink)" }}>{email}</strong>.
                  Close this tab anytime — we'll send the activation email
                  there too.
                </p>
              </div>

              <div className="card padded">
                <div className="lbl" style={{ marginBottom: 8 }}>
                  While you wait
                </div>
                <p
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink-mid)",
                    lineHeight: 1.55,
                    marginBottom: 12,
                  }}
                >
                  See what a finished briefing looks like before you start
                  running your own.
                </p>
                <button
                  className="btn btn-ghost btn-sm btn-block"
                  style={{ justifyContent: "space-between" }}
                  onClick={() => nav("/brief/demo")}
                >
                  <span className="row gap-2 center">
                    <IconSparkle /> Read sample · ABB India
                  </span>
                  <IconArrowRight />
                </button>
              </div>

              <div
                className="card padded"
                style={{
                  background: "var(--brand-deep)",
                  color: "#fff",
                  borderColor: "transparent",
                }}
              >
                <div
                  className="lbl"
                  style={{ color: "#9ddc78", marginBottom: 8 }}
                >
                  Need to reach us?
                </div>
                <p
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,.82)",
                    marginBottom: 12,
                  }}
                >
                  If something looks off, or you'd like to nudge the review,
                  we're a message away.
                </p>
                <button
                  className="btn btn-sm"
                  style={{
                    background: "#fff",
                    color: "var(--brand-deep)",
                    padding: "7px 11px",
                  }}
                >
                  <IconWA /> WhatsApp +91 77570 25257
                </button>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 28,
              textAlign: "center",
              fontSize: 11,
              color: "var(--ink-light)",
              fontFamily: "var(--mono)",
              letterSpacing: ".10em",
            }}
          >
            Educational technology tool — not a SEBI-registered RA or IA.
          </div>
        </div>
      </main>
    </div>
  );
}
